import * as React from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { CheckCircle2, Loader2, ShieldCheck, ShieldX, Sparkles, X } from "lucide-react";
import {
  Button,
  Checkbox,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import {
  ApiError,
  PERMISSION_GROUPS,
  ROLE_DEFAULT_PERMISSIONS,
  allowedPermissionIdsForTenant,
  filterGroupsForTenant,
  resolveRoleCeiling,
  type Designation,
  type DesignationCreateInput,
  type DesignationSuggestScopesResponse,
  type DesignationUpdateInput,
  type PermissionId,
  type UserRole,
} from "@eduviaos/shared";
import { useEnabledModules } from "@/hooks/useEnabledModules";
import { clientFacingText, roleLabelFor } from "@/lib/verticalProfile";
import {
  useCreateDesignation,
  useSuggestDesignationScopes,
  useUpdateDesignation,
  type UseUpdateDesignationVars,
} from "@/hooks/useDesignations";
import { SuggestPermissionsDrawer } from "@/components/SuggestPermissionsDrawer";

/**
 * Phase F1 — Designation editor sheet.
 *
 * Used in two modes:
 *   - mode="create" → renders a blank sheet, calls POST on save.
 *   - mode="edit"   → renders prefilled from `existing`, calls PUT.
 *
 * Surface contract:
 *   - Three sections: Identity → Base role (ceiling) → Permission overrides.
 *   - Out-of-ceiling permissions render disabled with a tooltip explaining
 *     which role they require — NEVER hidden silently. The editor stays
 *     honest about what's possible per Iron Rule #2.
 *   - Save is gated on a non-empty name (BE rejects empty anyway, but we
 *     short-circuit so the user doesn't need a network round-trip to learn
 *     about it).
 *   - On 422 `AZ_OVERRIDE_EXCEEDS_CEILING` (BE re-clamps in case the user
 *     races a role-change with a stale checkbox state) we surface the
 *     offending permission ids in the inline error and keep the sheet
 *     open so the user can correct without losing their work.
 *
 * Editor caps the IA's authoring ceiling at the IA's OWN permission
 * ceiling — we don't expose `super_admin` / `owner` as base-role options
 * from this surface (those are SA-only). A future SA-side editor can
 * lift this cap without touching this component.
 */

const ROLE_OPTIONS_FOR_IA: ReadonlyArray<{ value: UserRole; label: string; helper: string }> = [
  {
    value: "admin",
    label: "Admin",
    helper:
      "Institution admin ceiling — broadest access for leadership. Most designations under this anchor are operational heads (Principal, Vice-Principal, Centre Manager).",
  },
  {
    value: "teacher",
    label: "Teacher",
    helper:
      "Classroom-facing ceiling — assessment authoring, grading, attendance, parent communication. Use for HOD, Senior Faculty, Lab Incharge.",
  },
  {
    value: "staff",
    label: "Staff",
    helper:
      "Read-only operational ceiling — visibility into students, attendance, fees, announcements without write access. Use for Front Office, Reception, Counsellor.",
  },
];

export interface DesignationEditorSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When supplied, the sheet renders in edit mode and the save call is a PUT. */
  existing?: Designation | null;
  /** Optional callback fired AFTER the save succeeds. */
  onSaved?: (saved: Designation) => void;
}

interface DraftState {
  name: string;
  description: string;
  role: UserRole;
  /** Exact permission ids granted to members assigned this position. */
  granted: Set<PermissionId>;
}

function draftFromExisting(existing: Designation | null | undefined): DraftState {
  const role = (existing?.role ?? "staff") as UserRole;
  const ceiling = resolveRoleCeiling(role);
  const baseDefaults = ROLE_DEFAULT_PERMISSIONS[role];
  const defaultSet = new Set<PermissionId>(baseDefaults === "*" ? [] : baseDefaults);
  const granted = new Set<PermissionId>();
  for (const o of existing?.permission_overrides ?? []) {
    if (ceiling.has(o as PermissionId)) {
      granted.add(o as PermissionId);
    }
  }
  if (!existing) {
    defaultSet.forEach((permission) => granted.add(permission));
  }
  return {
    name: existing?.name ?? "",
    description: existing?.description ?? "",
    role,
    granted,
  };
}

export function DesignationEditorSheet({
  open,
  onOpenChange,
  existing,
  onSaved,
}: DesignationEditorSheetProps) {
  const isEdit = Boolean(existing);
  const reduceMotion = useReducedMotion();
  const [draft, setDraft] = React.useState<DraftState>(() => draftFromExisting(existing));
  const [error, setError] = React.useState<string | null>(null);
  const [invalidPermissions, setInvalidPermissions] = React.useState<string[]>([]);
  /**
   * Phase H3 (2026-05-10) — replaced the F2 auto-merge pattern with
   * an explicit drawer-based pick/apply flow. The admin now sees the
   * AI's suggestions one-at-a-time as checkboxes (pre-checked) inside
   * `<SuggestPermissionsDrawer>`, can uncheck individuals or use bulk
   * select-all / clear, and only commits the curated set when they
   * click Apply. The old "applied N permissions silently" UX hid
   * what the AI was actually granting; this matches the user's H3
   * spec: "...there should also be a apply / reject button with
   * suggestions with checkbox for bulk apply / reject or individual
   * apply reject..."
   */
  const [suggestDrawerOpen, setSuggestDrawerOpen] = React.useState(false);
  const [suggestionResponse, setSuggestionResponse] =
    React.useState<DesignationSuggestScopesResponse | null>(null);
  const [suggestionError, setSuggestionError] = React.useState<string | null>(null);
  const [suggestionAppliedSummary, setSuggestionAppliedSummary] = React.useState<{
    appliedAt: number;
    count: number;
  } | null>(null);
  const createMutation = useCreateDesignation();
  const updateMutation = useUpdateDesignation();
  const suggestMutation = useSuggestDesignationScopes();
  const isPending = createMutation.isPending || updateMutation.isPending;
  const isSuggesting = suggestMutation.isPending;

  // Re-seed the draft whenever the sheet (re)opens or the `existing` row
  // changes — closing then reopening for a different row should not
  // surface yesterday's draft. Also clear any stale suggestion so the
  // drawer doesn't carry across a sheet reopen for a different row.
  React.useEffect(() => {
    if (open) {
      setDraft(draftFromExisting(existing));
      setError(null);
      setInvalidPermissions([]);
      setSuggestDrawerOpen(false);
      setSuggestionResponse(null);
      setSuggestionError(null);
      setSuggestionAppliedSummary(null);
      suggestMutation.reset();
    }
    // suggestMutation.reset is stable across renders — TanStack Query's
    // mutation result objects are stable for a given mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existing]);

  const ceiling = React.useMemo(() => resolveRoleCeiling(draft.role), [draft.role]);
  const defaultSet = React.useMemo(() => {
    const raw = ROLE_DEFAULT_PERMISSIONS[draft.role];
    return new Set<PermissionId>(raw === "*" ? Array.from(ceiling) : raw);
  }, [draft.role, ceiling]);

  // Phase F-finale-3 — hide permission groups + rows that don't apply
  // to the calling tenant's vertical / enabled-module mix. While the
  // dashboard summary is still loading we render the full catalog so
  // the editor never blank-flashes; once the vertical resolves we
  // narrow to the gated subset.
  const { vertical, enabledKeys: enabledModules } = useEnabledModules();
  const visibleGroups = React.useMemo(() => {
    if (!vertical) return PERMISSION_GROUPS;
    return filterGroupsForTenant(PERMISSION_GROUPS, {
      vertical,
      enabledModules,
    });
  }, [vertical, enabledModules]);

  // Defence-in-depth: when the BE AI-suggest returns a permission that
  // the FE already gates away (e.g. `manage_zones` on a coaching
  // tenant — only possible if BE filtering lags behind FE), silently
  // drop it before handing the list to the drawer so the admin never
  // sees a checkbox they could tick into a guaranteed BE 422.
  const allowedIds = React.useMemo(() => {
    if (!vertical) return null;
    return allowedPermissionIdsForTenant(PERMISSION_GROUPS, {
      vertical,
      enabledModules,
    });
  }, [vertical, enabledModules]);

  const filterSuggestionsByGate = React.useCallback(
    (ids: ReadonlyArray<string>): string[] => {
      if (!allowedIds) return Array.from(ids);
      return ids.filter((id) => allowedIds.has(id as PermissionId));
    },
    [allowedIds],
  );

  // When the user changes the base role, drop any granted permissions
  // that fell out of the new ceiling. This keeps the sheet's state
  // honest with what the BE would accept on save.
  const onRoleChange = (next: UserRole) => {
    const nextCeiling = resolveRoleCeiling(next);
    const filtered = new Set<PermissionId>();
    for (const g of draft.granted) {
      if (nextCeiling.has(g)) {
        filtered.add(g);
      }
    }
    setDraft({ ...draft, role: next, granted: filtered });
  };

  /**
   * Phase H3 (2026-05-10) — drawer-based suggest flow.
   *
   * Replaces the F2 auto-merge pattern. We:
   *   1. Validate the label client-side (the BE accepts an empty
   *      label but the AI is useless without it).
   *   2. Open the drawer in the "loading" state immediately so the
   *      admin gets a focus-trapped UI while the network round-trip
   *      runs.
   *   3. Fire the AI mutation. On success, the drawer renders the
   *      checkbox list (all pre-checked) inside the drawer's
   *      `populated` state. On error, the drawer flips to the
   *      `error` state with a Retry button.
   *
   * The actual merge into `draft.granted` happens later, in
   * `applySuggestion`, only when the admin clicks Apply inside the
   * drawer with their final curated set. This is the user's H3 spec:
   *
   *   > "...there should also be a apply / reject button with
   *   > suggestions with checkbox for bulk apply / reject or
   *   > individual apply reject..."
   */
  const onSuggest = async () => {
    setError(null);
    setInvalidPermissions([]);
    setSuggestionError(null);
    const trimmedLabel = draft.name.trim();
    if (!trimmedLabel) {
      setError("Add a label first so the AI knows what to suggest.");
      return;
    }
    setSuggestionResponse(null);
    setSuggestDrawerOpen(true);
    try {
      const result = await suggestMutation.mutateAsync({
        label: trimmedLabel,
        role: draft.role,
        // vertical omitted → BE derives from auth context's tenant.
      });
      setSuggestionResponse(result);
    } catch (raw) {
      // The drawer renders an inline error banner with a Retry CTA —
      // we surface a human-readable message there rather than on the
      // editor canvas behind the drawer.
      if (raw instanceof ApiError) {
        if (raw.code === "AI_UNAVAILABLE") {
          setSuggestionError(
            "AI is temporarily unavailable. Try again in a moment, or set the overrides manually.",
          );
        } else {
          setSuggestionError(raw.message || "Could not get an AI suggestion.");
        }
      } else if (raw instanceof Error && raw.message) {
        setSuggestionError(raw.message);
      } else {
        setSuggestionError("Could not get an AI suggestion.");
      }
    }
  };

  /**
   * Phase H3 — commit the curated set of AI-suggested permissions.
   *
   * Merges the `selected` ids into `draft.granted` after re-clamping
   * against the ceiling and skipping default-on permissions. This is
   * the same defensive filter the F2 merge used; it survives the
   * unlikely race of an admin changing the base role while the AI
   * round-trip was in flight.
   */
  const applySuggestion = (selected: string[]) => {
    setDraft((prev) => {
      const next = new Set(prev.granted);
      for (const perm of selected) {
        const permId = perm as PermissionId;
        if (!ceiling.has(permId)) continue;
        next.add(permId);
      }
      return { ...prev, granted: next };
    });
    setSuggestionAppliedSummary({
      appliedAt: Date.now(),
      count: selected.length,
    });
  };

  const dismissAppliedSummary = () => {
    setSuggestionAppliedSummary(null);
  };

  const togglePermission = (id: PermissionId, checked: boolean) => {
    if (!ceiling.has(id)) {
      // Defensive — UI should already gate this, but if a bug fires
      // through, refuse silently to avoid sending an invalid override.
      return;
    }
    const next = new Set(draft.granted);
    if (checked) next.add(id);
    else next.delete(id);
    setDraft({ ...draft, granted: next });
  };

  const onSave = async () => {
    setError(null);
    setInvalidPermissions([]);
    const trimmedName = draft.name.trim();
    if (!trimmedName) {
      setError("Name is required.");
      return;
    }
    if (trimmedName.length > 80) {
      setError("Name must be at most 80 characters.");
      return;
    }
    if (draft.description.length > 400) {
      setError("Description must be at most 400 characters.");
      return;
    }

    const payload: DesignationCreateInput = {
      name: trimmedName,
      role: draft.role,
      permission_mode: "replace",
      permission_overrides: Array.from(draft.granted).sort(),
      description: draft.description.trim() ? draft.description.trim() : null,
    };

    try {
      if (isEdit && existing) {
        const updateBody: DesignationUpdateInput = payload;
        const vars: UseUpdateDesignationVars = { id: existing.id, body: updateBody };
        const saved = await updateMutation.mutateAsync(vars);
        onSaved?.(saved);
        // Phase E1.b.2 — global toast on success; in-sheet `role="alert"`
        // banner (with structured permission ceiling / duplicate-name
        // recovery copy) stays for validation failures so the admin
        // can react without losing context inside the form.
        toast.success(`Saved changes to ${saved.name}.`);
      } else {
        const saved = await createMutation.mutateAsync(payload);
        onSaved?.(saved);
        toast.success(`Created designation ${saved.name}.`);
      }
      onOpenChange(false);
    } catch (raw) {
      // The BE wraps all errors in the ErrorEnvelope: { error: { code, message, details } }.
      // ApiError lifts `code` to the top of the FE shape, but tags like
      // `AZ_OVERRIDE_EXCEEDS_CEILING` ride inside `details.code` (the
      // top-level code is the generic `VALIDATION_ERROR`). See
      // app/domains/auth/designations.py:131.
      if (raw instanceof ApiError) {
        const subCode = typeof raw.details?.code === "string" ? (raw.details.code as string) : null;
        if (subCode === "AZ_OVERRIDE_EXCEEDS_CEILING") {
          const invalidRaw = raw.details?.invalid;
          const invalid = Array.isArray(invalidRaw)
            ? invalidRaw.filter((x): x is string => typeof x === "string")
            : [];
          setError("Some permissions exceed the role's ceiling.");
          setInvalidPermissions(invalid);
        } else if (raw.code === "DUPLICATE_NAME") {
          setError("A designation with this label already exists at your institution.");
        } else {
          setError(raw.message);
        }
      } else if (raw instanceof Error && raw.message) {
        setError(raw.message);
      } else {
        setError("Could not save the designation. Please try again.");
      }
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl">
        <SheetHeader className="border-border/60 border-b p-6">
          <SheetTitle>{isEdit ? `Edit ${existing?.name}` : "Author a designation"}</SheetTitle>
          <SheetDescription>
            Designations are institution-defined labels (e.g. "Senior Counsellor", "PG Coordinator")
            anchored to a security ceiling. The permissions below are the exact access assigned to a
            member — never above the administrator&apos;s own authority.
          </SheetDescription>
        </SheetHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
          <section className="mb-8 space-y-4">
            <h3 className="text-muted-foreground text-sm font-semibold uppercase tracking-wide">
              1. Identity
            </h3>
            <div className="space-y-2">
              <Label htmlFor="designation-name">
                Label <span className="text-destructive">*</span>
              </Label>
              <Input
                id="designation-name"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="e.g. Senior Counsellor — South Wing"
                maxLength={80}
                autoFocus={!isEdit}
              />
              <p className="text-muted-foreground text-xs">
                Free-form label visible on the Members tab and in invitation emails. Up to 80
                characters.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="designation-description">
                Description <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Textarea
                id="designation-description"
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                placeholder="What this role is responsible for. Helps colleagues understand the boundary."
                maxLength={400}
                rows={3}
              />
              <p className="text-muted-foreground text-xs">
                Shown on the designation list. Up to 400 characters.
              </p>
            </div>
          </section>

          <section className="mb-8 space-y-4">
            <div>
              <h3 className="text-muted-foreground text-sm font-semibold uppercase tracking-wide">
                2. Base role (ceiling)
              </h3>
              <p className="text-muted-foreground mt-2 text-xs">
                Pick the highest class of access this position may hold. This is a safety ceiling,
                not an automatic grant.
              </p>
            </div>
            <RadioGroup
              value={draft.role}
              onValueChange={(v) => onRoleChange(v as UserRole)}
              className="space-y-2"
            >
              {ROLE_OPTIONS_FOR_IA.map((opt) => {
                const label = roleLabelFor(vertical ?? "school", opt.value, opt.label);
                const helper =
                  vertical === "study_centre" && opt.value === "teacher"
                    ? "Mentor ceiling — attendance, learning, and day-to-day floor work. Use for a floor mentor or subject guide."
                    : vertical === "study_centre" && opt.value === "staff"
                      ? "Team ceiling — see learners, attendance, and fees without changing them. Use for reception or the front desk."
                      : clientFacingText(vertical ?? "school", opt.helper);
                return (
                  <Label
                    key={opt.value}
                    htmlFor={`role-${opt.value}`}
                    className={
                      "border-border/60 flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors " +
                      (draft.role === opt.value
                        ? "border-primary bg-primary/5"
                        : "hover:bg-muted/40")
                    }
                  >
                    <RadioGroupItem value={opt.value} id={`role-${opt.value}`} className="mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <p className="text-foreground text-sm font-medium">{label}</p>
                      <p className="text-muted-foreground mt-0.5 text-xs leading-relaxed">
                        {helper}
                      </p>
                    </div>
                  </Label>
                );
              })}
            </RadioGroup>
          </section>

          <section className="space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <h3 className="text-muted-foreground text-sm font-semibold uppercase tracking-wide">
                  3. Exact access
                </h3>
                <p className="text-muted-foreground mt-2 text-xs">
                  <ShieldCheck aria-hidden="true" className="mr-1 inline size-3 text-emerald-500" />
                  <span className="text-foreground">Recommended</span> permissions start checked for
                  a new position, and you can remove any of them.{" "}
                  <ShieldX
                    aria-hidden="true"
                    className="text-muted-foreground/60 ml-2 mr-1 inline size-3"
                  />
                  <span className="text-foreground">Out of ceiling</span> permissions need a higher
                  base role; we keep them visible (greyed out) so you can see what's possible.
                </p>
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={onSuggest}
                disabled={isSuggesting || isPending}
                aria-label={`Suggest permission overrides for ${draft.name || "this designation"}`}
                data-testid="designation-suggest-scopes-btn"
                className={cn(
                  "shrink-0 gap-1.5",
                  // Subtle violet hint so the AI affordance reads as
                  // a distinct surface from the standard outline button —
                  // matches the AISuggestionChip's iridescent border.
                  "border-violet-300/60 text-violet-800 hover:bg-violet-50",
                  "dark:border-violet-500/40 dark:text-violet-200 dark:hover:bg-violet-950/40",
                )}
              >
                {isSuggesting ? (
                  <Loader2 aria-hidden="true" className="size-3.5 animate-spin" />
                ) : (
                  <Sparkles aria-hidden="true" className="size-3.5" />
                )}
                {isSuggesting ? "Thinking…" : "Suggest scopes"}
              </Button>
            </div>

            <AnimatePresence initial={false}>
              {suggestionAppliedSummary ? (
                <motion.div
                  key="suggestion-applied-pill"
                  initial={reduceMotion ? false : { opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.18, ease: "easeOut" }}
                  className={cn(
                    "flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-xs",
                    "border-violet-300/60 bg-gradient-to-br from-violet-50/60 to-transparent",
                    "dark:border-violet-500/30 dark:from-violet-950/20",
                  )}
                  data-testid="designation-suggestion-applied"
                  aria-live="polite"
                >
                  <span className="flex items-center gap-2">
                    <CheckCircle2
                      aria-hidden="true"
                      className="size-3.5 text-violet-700 dark:text-violet-300"
                    />
                    <span className="text-foreground font-medium">
                      Applied {suggestionAppliedSummary.count} AI-suggested permission
                      {suggestionAppliedSummary.count === 1 ? "" : "s"} below.
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={dismissAppliedSummary}
                    className="text-muted-foreground hover:text-foreground rounded p-0.5 transition-colors"
                    aria-label="Dismiss applied-suggestion notice"
                  >
                    <X className="size-3.5" aria-hidden="true" />
                  </button>
                </motion.div>
              ) : null}
            </AnimatePresence>

            <TooltipProvider delayDuration={150}>
              <div className="space-y-5">
                {visibleGroups.map((group) => (
                  <fieldset key={group.key} className="border-border/60 rounded-lg border p-4">
                    <legend className="text-foreground px-1 text-sm font-medium">
                      {clientFacingText(vertical ?? "school", group.label)}
                    </legend>
                    <p className="text-muted-foreground mb-3 text-xs">
                      {clientFacingText(vertical ?? "school", group.description)}
                    </p>
                    <div className="space-y-2">
                      {group.permissions.map((perm) => {
                        const inCeiling = ceiling.has(perm.id);
                        const isDefault = defaultSet.has(perm.id);
                        const isGranted = draft.granted.has(perm.id);
                        const isChecked = isGranted;
                        const isDisabled = !inCeiling;
                        const isInvalid = invalidPermissions.includes(perm.id);
                        const tooltipText = !inCeiling
                          ? `Requires ceiling: admin (or higher)`
                          : null;
                        const row = (
                          <motion.label
                            key={perm.id}
                            htmlFor={`perm-${perm.id}`}
                            initial={false}
                            animate={
                              isInvalid && !reduceMotion ? { x: [-3, 3, -3, 3, 0] } : undefined
                            }
                            transition={{ duration: 0.32 }}
                            className={
                              "flex cursor-pointer items-start gap-3 rounded-md p-2 transition-colors " +
                              (isInvalid
                                ? "bg-destructive/10"
                                : isGranted
                                  ? "bg-primary/5"
                                  : "hover:bg-muted/30") +
                              (isDisabled ? " cursor-not-allowed opacity-60" : "")
                            }
                          >
                            <Checkbox
                              id={`perm-${perm.id}`}
                              checked={isChecked}
                              disabled={isDisabled}
                              onCheckedChange={(checked) =>
                                togglePermission(perm.id, checked === true)
                              }
                              className="mt-0.5"
                            />
                            <div className="min-w-0 flex-1">
                              <p className="text-foreground text-sm font-medium">
                                {clientFacingText(vertical ?? "school", perm.label)}
                              </p>
                              <p className="text-muted-foreground mt-0.5 text-xs">
                                {clientFacingText(vertical ?? "school", perm.helper)}
                              </p>
                              {isDefault ? (
                                <p className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600 dark:text-emerald-400">
                                  <ShieldCheck aria-hidden="true" className="size-3" />
                                  Recommended for{" "}
                                  {roleLabelFor(vertical ?? "school", draft.role, draft.role)}
                                </p>
                              ) : !inCeiling ? (
                                <p className="text-muted-foreground/80 mt-1 inline-flex items-center gap-1 text-[11px]">
                                  <ShieldX aria-hidden="true" className="size-3" />
                                  Requires admin ceiling
                                </p>
                              ) : null}
                            </div>
                          </motion.label>
                        );
                        return tooltipText ? (
                          <Tooltip key={perm.id}>
                            <TooltipTrigger asChild>{row}</TooltipTrigger>
                            <TooltipContent side="left">{tooltipText}</TooltipContent>
                          </Tooltip>
                        ) : (
                          row
                        );
                      })}
                    </div>
                  </fieldset>
                ))}
              </div>
            </TooltipProvider>
          </section>
        </div>

        <div className="border-border/60 flex flex-col gap-3 border-t p-6">
          {error ? (
            <div className="bg-destructive/10 text-destructive rounded-md px-3 py-2 text-sm">
              {error}
              {invalidPermissions.length > 0 ? (
                <span className="text-destructive/80 mt-1 block text-xs">
                  Affected: {invalidPermissions.join(", ")}
                </span>
              ) : null}
            </div>
          ) : null}
          <div className="flex items-center justify-end gap-2">
            <SheetClose asChild>
              <Button variant="outline" disabled={isPending}>
                Cancel
              </Button>
            </SheetClose>
            <Button onClick={onSave} disabled={isPending}>
              {isPending ? (
                <>
                  <Loader2 aria-hidden="true" className="size-4 animate-spin" />
                  Saving…
                </>
              ) : isEdit ? (
                "Save changes"
              ) : (
                "Create designation"
              )}
            </Button>
          </div>
        </div>
      </SheetContent>

      {/* Phase H3 — AI-suggest drawer rendered as a sibling so its
          own focus-trapped portal layers above the editor sheet. The
          drawer is purely a picker; the actual merge into
          `draft.granted` happens in `applySuggestion` when the admin
          clicks Apply inside the drawer. */}
      <SuggestPermissionsDrawer
        open={suggestDrawerOpen}
        onOpenChange={(next) => {
          setSuggestDrawerOpen(next);
          if (!next) {
            // Closing the drawer voids the in-flight error banner so
            // a future "Suggest scopes" click starts clean.
            setSuggestionError(null);
          }
        }}
        title={`Suggest scopes for ${draft.name.trim() || "this designation"}`}
        subtitle={`Based on the "${draft.role}" base role and the label you entered. Uncheck anything you don't want; click Apply when you're happy.`}
        status={
          isSuggesting
            ? "loading"
            : suggestionError
              ? "error"
              : suggestionResponse
                ? "success"
                : "idle"
        }
        suggestions={filterSuggestionsByGate(suggestionResponse?.suggested_overrides ?? [])}
        rationales={suggestionResponse?.rationales ?? {}}
        ceilingTruncated={suggestionResponse?.ceiling_truncated ?? []}
        unknownDropped={suggestionResponse?.unknown_dropped ?? []}
        provider={suggestionResponse?.provider}
        model={suggestionResponse?.model}
        fallbackDepth={suggestionResponse?.fallback_depth ?? 0}
        parseError={suggestionResponse?.parse_error ?? null}
        errorMessage={suggestionError}
        onRetry={() => {
          void onSuggest();
        }}
        onApply={(selected) => {
          applySuggestion(selected);
        }}
      />
    </Sheet>
  );
}
